// @flow
import * as React from 'react';
import {useState, useEffect} from 'react';
import {AlignBox} from 'sail/Align';
import Grid, {GridItem} from 'sail/Grid';
import {Text} from 'sail/Text';
import Box from 'sail/Box';
import FlagIcon, {FLAG_COUNTRY_CODES} from 'sail/FlagIcon';
import Badge from 'sail/Badge';
import {OnlyRenderOnClient} from 'docs/components';

import type {FlagCountryCode} from 'sail/FlagIcon';
import {isBrowser} from 'docs/util';
import type {GridColumns} from 'sail/Grid/GridItem';
import type {CountryDisplayName} from '../CountryName/CountryName';

function getFlagCountryCodeType(code: string): FlagCountryCode {
  return FLAG_COUNTRY_CODES[code.toUpperCase()] || FLAG_COUNTRY_CODES.unknown;
}

function getCountryName(
  country: FlagCountryCode,
  countryDisplayNames: Array<CountryDisplayName>,
): string {
  return countryDisplayNames.find((c) => c.code === country)?.name || country;
}

type CountryFlagListProps = {|
  countries: Array<FlagCountryCode>,
  betaCountries?: Array<FlagCountryCode>,
  asteriskCountries?: Array<FlagCountryCode>,
  countryDisplayNames: Array<CountryDisplayName>,
  size?: number,
|};

export const CountryFlagList = ({
  countries,
  betaCountries = [],
  asteriskCountries = [],
  countryDisplayNames,
  size = 20,
}: CountryFlagListProps) => {
  const DEFAULT_COLUMN_SPAN: GridColumns = ((countries.length < 4
    ? Math.floor(12 / countries.length)
    : 3): any);
  const COLUMN_SPAN: GridColumns =
    isBrowser && window.innerWidth <= 600 ? 4 : DEFAULT_COLUMN_SPAN;
  const [sizedColumnSpan, setSizedColumnSpan] = useState(COLUMN_SPAN);
  useEffect(() => {
    const updateColumnSpan = () => {
      const conditionalColumnSpan: GridColumns =
        isBrowser && window.innerWidth <= 600 ? 4 : DEFAULT_COLUMN_SPAN;
      setSizedColumnSpan(conditionalColumnSpan);
    };
    window.addEventListener('resize', updateColumnSpan);
    updateColumnSpan(); // <-- call updateColumnSpan here to set the right column on page load

    return () => {
      // Clean up prior listener if it exists.
      window.removeEventListener('resize', updateColumnSpan);
    };
  }, [DEFAULT_COLUMN_SPAN]);

  // Convert string to FlagCountryCode, since FlagIcon does not accept a string.
  const countryFlagCodes = countries.map((country) =>
    getFlagCountryCodeType(country),
  );

  const sortedCountries = countryFlagCodes.sort((c1, c2) =>
    getCountryName(c1, countryDisplayNames).localeCompare(
      getCountryName(c2, countryDisplayNames),
    ),
  );

  // calculating total number of columns and rows needed
  const totalColumns = 12 / sizedColumnSpan;
  const totalRows = Math.ceil(countries.length / totalColumns);

  return (
    <OnlyRenderOnClient>
      <Box padding={8} className="CountryFlagList">
        <Grid gap={8}>
          {sortedCountries.map((country, index) => {
            // calculate, based on index, which row/column we are on
            const column = Math.floor(index / totalRows);
            const row = index - (column - 1) * totalRows + 1;
            const columnSized: GridColumns = (column * sizedColumnSpan +
              1: any);
            return (
              <GridItem
                key={country}
                columnSpan={sizedColumnSpan}
                column={columnSized}
                row={row}
              >
                <AlignBox
                  reference={{size: 16, lineHeight: 26}}
                  style={{height: '100%'}}
                >
                  <Box margin={{right: 8}}>
                    <FlagIcon countryCode={country} size={size} />
                  </Box>
                  <Text size={16} lineHeight={24}>
                    {getCountryName(country, countryDisplayNames)}
                    {asteriskCountries.includes(country) && '*'}
                    {betaCountries.includes(country) && (
                      <Badge label={'Preview'} />
                    )}
                  </Text>
                </AlignBox>
              </GridItem>
            );
          })}
        </Grid>
      </Box>
    </OnlyRenderOnClient>
  );
};
